import { CreateForm } from 'framework/form';
import { useParams } from 'react-router-dom';
import ChangeRequestForm from '../ChangeRequestForm';
import { ShowSection } from 'framework/ui/layout';
import ChangeRequestActionButtons from '../ChangeRequestActionButtons';
import {
  CHANGE_REQUEST_STATUS,
  useProjectFinanceService,
  useNewProjectService,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import useChangeRequestService from 'domain/projects/newProject/finance/changeRequests/changeRequestService';
import useMilestonesService from 'domain/projects/newProject/milestones/useMilestonesService';
import useBillingService, {
  BillingScheduleResponse,
  ScheduleItemStatus,
} from 'domain/projects/newProject/finance/billing/billingService';

export const removeGuidelinesFromContent = (content: string) => {
  if (!content) return content;

  const cleanContent = content.replace(/\{\{\sguideline(\d+)\s\}\}/g, '');

  return cleanContent.trim();
};

const CreateChangeRequestLayout = () => {
  const { milestonesBatch } = useMilestonesService();
  const { saveSensitiveProjectInfo, getBudget } = useProjectFinanceService();
  const { getFinanceUrl } = useNewProjectUrl();
  const { createChangeRequest, getChangeRequestValidationSchema } = useChangeRequestService();
  const { billingScheduleSearch } = useBillingService();
  const { getById } = useNewProjectService();

  const { id } = useParams();
  return (
    <CreateForm
      getDataFunc={async () => {
        const billingScheduleResponse = await billingScheduleSearch({
          pageSize: 100,
          filter: { projectId: { eq: id! } },
        });

        const billingSchedule: BillingScheduleResponse | undefined = billingScheduleResponse?.result?.[0];
        let billingType: string | null = billingSchedule?.billingType || null;

        if (!billingType) {
          try {
            const projectData = await getById(id!);
            const budgetResponse = projectData?.defaultBudgetId
              ? await getBudget(projectData.defaultBudgetId).catch(() => null)
              : null;
            billingType = budgetResponse?.billingType || null;
          } catch {}
        }

        return {
          milestones: billingSchedule?.scheduleItems?.filter(item => item.status === ScheduleItemStatus.PLANNED) || [],
          milestoneSchedule: false,
          billingScheduleId: billingSchedule?.id || null,
          hasBillingSchedule: Boolean(billingSchedule),
          billingType,
        };
      }}
      submitFormFunc={async values => {
        const createdAdditionalBudgetAmountId = values.additionalBudgetAmount
          ? await saveSensitiveProjectInfo(values.additionalBudgetAmount).then(response => response.id)
          : null;

        const cleanRawContent = removeGuidelinesFromContent(values.rawContent);
        return Promise.all([
          ...(values.milestoneSchedule ? [milestonesBatch(id!, values.milestones)] : []),
          createChangeRequest(id!, {
            title: values.title,
            status: values.isDraft ? CHANGE_REQUEST_STATUS.DRAFT : CHANGE_REQUEST_STATUS.IN_APPROVAL,
            additionalHours: Number(values.additionalHours),
            additionalBudgetAmountId: createdAdditionalBudgetAmountId,
            additionalFte: Number(values.additionalFte),
            receiverFullName: values.receiverFullName,
            receiverEmail: values.receiverEmail,
            receiverTitle: values.receiverTitle,
            newDeliveryDate: values.newDeliveryDate,
            billingType: values.billingType,
            billingMilestoneId: values.billingMilestoneId || null,
            rawContent: values.isDraft ? values.rawContent : cleanRawContent,
            companyName: values.companyName,
          }),
        ]);
      }}
      afterSubmit={{
        redirect: getFinanceUrl(id),
        message: 'You are awesome! Change request has been successfully created.',
      }}
      getValidationSchemaFunc={getChangeRequestValidationSchema}
    >
      <ShowSection title="Add New Change Request:" titleVariant="h2">
        <ChangeRequestForm projectId={id} />
        <ChangeRequestActionButtons />
      </ShowSection>
    </CreateForm>
  );
};

export default CreateChangeRequestLayout;
